import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useT } from '../../lib/i18n'
import { useQuery, type Report } from '../../lib/api'

const TYPE_COLOR: Record<string, string> = {
  ok:      'var(--health-good)',
  error:   'var(--health-crit)',
  warning: 'var(--health-warn)',
  action:  'var(--accent)',
  commit:  'var(--chart-git)',
}
const TYPE_BADGE: Record<string, string> = {
  ok: 'OK', error: 'ERR', warning: 'WRN', action: 'ACT', commit: 'GIT',
}

export default function Journal() {
  const t = useT()
  const [filterProject, setFilterProject] = useState('')
  const [filterType, setFilterType] = useState('')
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [reports, setReports] = useState<Report[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)

  const url = `/timeline?limit=50${cursor ? `&cursor=${cursor}` : ''}`
  const { data, loading, error } = useQuery<{ reports: Report[]; nextCursor: string | null }>(url)

  useEffect(() => {
    if (data) {
      setReports((prev) => cursor ? [...prev, ...data.reports] : data.reports)
      setNextCursor(data.nextCursor)
    }
  }, [data])

  const bottomRef = useRef<HTMLDivElement>(null)
  const loadMore = useCallback(() => {
    if (nextCursor && !loading) setCursor(nextCursor)
  }, [nextCursor, loading])

  const visible = reports.filter((r) => {
    if (filterProject && r.projectId !== filterProject) return false
    if (filterType && r.status !== filterType) return false
    return true
  })

  const projects = [...new Set(reports.map((r) => r.projectId))]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <select
          value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)}
          style={selectStyle}
        >
          <option value="">{t('all_projects')}</option>
          {projects.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={selectStyle}
        >
          <option value="">{t('all_types')}</option>
          {['ok', 'warning', 'error'].map((v) => (
            <option key={v} value={v}>{TYPE_BADGE[v]}</option>
          ))}
        </select>
      </div>

      {error && <div style={{ color: 'var(--health-crit)', fontSize: '12px' }}>{t('error_fetch')}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
        {visible.map((r) => (
          <div
            key={r.id}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              padding: '8px 4px',
              borderBottom: '1px solid var(--border)',
              fontSize: '13px',
            }}
          >
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '36px', height: '18px', borderRadius: '3px',
              background: (TYPE_COLOR[r.status] ?? 'var(--text-muted)') + '22',
              color: TYPE_COLOR[r.status] ?? 'var(--text-muted)',
              fontSize: '10px', fontFamily: 'var(--font-mono)', fontWeight: 700,
              flexShrink: 0, marginTop: '1px',
            }}>
              {TYPE_BADGE[r.status] ?? r.status.toUpperCase()}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-dim)' }}>
                  {r.startedAt.slice(11, 19)}
                </span>
                <span style={{ fontSize: '11px', color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
                  {r.projectId}
                </span>
                <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                  {r.branch}
                </span>
              </div>
              {r.summaryMd && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.summaryMd.replace(/^#+\s*/m, '').slice(0, 120)}
                </div>
              )}
              {r.metrics.errors > 0 && (
                <div style={{ fontSize: '11px', color: 'var(--health-crit)', marginTop: '2px' }}>
                  ⚠ {r.metrics.errors} erreur{r.metrics.errors > 1 ? 's' : ''} · taux {r.metrics.successRate}%
                </div>
              )}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
              {r.metrics.actions} {t('actions').toLowerCase()}
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
          {t('loading')}
        </div>
      )}

      {nextCursor && !loading && (
        <button onClick={loadMore} style={btnStyle}>
          Charger plus…
        </button>
      )}

      {visible.length === 0 && !loading && (
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
          {t('no_data')}
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  )
}

const selectStyle: React.CSSProperties = {
  padding: '5px 8px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: '12px',
  cursor: 'pointer',
}
const btnStyle: React.CSSProperties = {
  padding: '6px 16px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  background: 'var(--surface)',
  color: 'var(--text-muted)',
  fontFamily: 'var(--font-sans)',
  fontSize: '12px',
  cursor: 'pointer',
  margin: '0 auto',
  display: 'block',
}
